import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import cn from 'classnames';
import { getSmartImage } from '../../helpers/helpers';
import SessionViewer from './SessionViewer';
import './DispatcherBrain.scss';

const TABS = [
  { key: 'shopper-brief', label: 'Shopper Brief', className: 'tab-wide' },
  { key: 'sessions', label: 'Sessions', className: 'tab-narrow' }
];

const GRADE_LABELS = ['', 'Poor', 'Below Average', 'Average', 'Good', 'Excellent'];

const DispatcherBrain = ({
  selectedThread,
  brief,
  briefComplete,
  datakeeperResult,
  datakeeperLoading,
  sessions,
  sessionsLoading,
  productRecs = [],
  productRecsLoading,
  onFetchProductRecs,
  onApplyBio,
  onUpdateBio,
  onFetchSessions
}) => {
  const [activeTab, setActiveTab] = useState('shopper-brief');
  const [editingBio, setEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [viewingSession, setViewingSession] = useState(null);

  useEffect(() => {
    if (datakeeperLoading) {
      setActiveTab('shopper-brief');
    }
  }, [datakeeperLoading]);

  const handleTabChange = tab => {
    setActiveTab(tab);
    if (tab === 'sessions' && onFetchSessions) {
      onFetchSessions();
    }
  };

  const startEditBio = () => {
    setBioText(selectedThread?.testShopperBio || '');
    setEditingBio(true);
  };

  const saveBio = () => {
    if (selectedThread) {
      onUpdateBio(selectedThread.id, bioText);
      setEditingBio(false);
    }
  };

  const renderBriefSection = () => {
    if (!brief) {
      return (
        <div className='panel-section'>
          <div className='section-header'>Brief</div>
          <div className='empty-section'>Brief will appear here once the dispatcher completes intake</div>
        </div>
      );
    }

    const briefText = typeof brief === 'string' ? brief : JSON.stringify(brief, null, 2);

    return (
      <div className='panel-section'>
        <div className='section-header'>Brief</div>
        <div className='brief-content'>
          <div className='brief-status complete'>Brief complete — sent to curator</div>
          <div className='brief-text'>
            <ReactMarkdown>{briefText}</ReactMarkdown>
          </div>
        </div>
      </div>
    );
  };

  const renderProductRecs = () => {
    if (!brief) return null;

    return (
      <div className='panel-section'>
        <div className='section-header'>
          Product Recs
          <button className='fetch-recs-btn' onClick={() => onFetchProductRecs(brief)} disabled={productRecsLoading}>
            {productRecsLoading ? 'Loading...' : 'Fetch Recs'}
          </button>
        </div>
        {productRecsLoading ? (
          <div className='empty-section'>Loading product recommendations...</div>
        ) : !Array.isArray(productRecs) || productRecs.length === 0 ? (
          <div className='empty-section'>No product recommendations found</div>
        ) : (
          <div className='product-recs-grid'>
            {productRecs.map((product, i) => (
              <a key={product.id || i} className='product-rec-card' href={product.fallbackUrl} target='_blank' rel='noopener noreferrer'>
                {(() => {
                  const imgUrl = getSmartImage(product.images?.[0]?.image || product.image);
                  return imgUrl ? (
                    <img className='product-rec-image' src={imgUrl} alt={product.title || ''} />
                  ) : (
                    <div className='product-rec-image product-rec-image-placeholder' />
                  );
                })()}
                <div className='product-rec-info'>
                  {product.AllBrand_name && <div className='product-rec-brand'>{product.AllBrand_name}</div>}
                  <div className='product-rec-title'>{product.title || 'Untitled'}</div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderBioSection = () => {
    if (!selectedThread) return null;

    return (
      <div className='panel-section'>
        <div className='section-header'>Bio</div>
        <div className='bio-content'>
          <div className='bio-header'>
            <span className='bio-label'>testShopperBio</span>
            {!editingBio && (
              <button className='edit-btn' onClick={startEditBio}>
                Edit
              </button>
            )}
          </div>

          {editingBio ? (
            <div className='bio-editor'>
              <textarea value={bioText} onChange={e => setBioText(e.target.value)} rows={8} autoFocus />
              <div className='bio-editor-actions'>
                <button className='cancel-btn' onClick={() => setEditingBio(false)}>
                  Cancel
                </button>
                <button className='save-btn' onClick={saveBio}>
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className='bio-text'>{selectedThread.testShopperBio || <span className='placeholder'>No bio set for this test shopper</span>}</div>
          )}

          {datakeeperResult && (
            <div className='datakeeper-suggestion'>
              <h4>Datakeeper Analysis</h4>
              <span className={cn('suggestion-action', datakeeperResult.action)}>{datakeeperResult.action}</span>
              <p className='suggestion-reasoning'>{datakeeperResult.reasoning}</p>
              {datakeeperResult.updatedBio && (
                <div className='suggested-bio'>
                  <strong>Suggested Updated Bio:</strong>
                  <p className='bio-preview'>{datakeeperResult.updatedBio}</p>
                  {selectedThread?.testShopperBio === datakeeperResult.updatedBio ? (
                    <button className='apply-btn applied' disabled>
                      Applied
                    </button>
                  ) : (
                    <button className='apply-btn' onClick={() => onApplyBio(datakeeperResult.updatedBio)}>
                      Apply Update
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderShopperBriefTab = () => {
    if (!selectedThread) return <div className='empty-tab'>Select a shopper to view their brief</div>;

    return (
      <div className='shopper-brief-content'>
        {renderBriefSection()}
        {renderProductRecs()}
        {renderBioSection()}
      </div>
    );
  };

  const renderSessionsTab = () => {
    if (sessionsLoading) return <div className='empty-tab'>Loading sessions...</div>;
    if (!sessions || sessions.length === 0) return <div className='empty-tab'>No saved sessions yet</div>;

    return (
      <div className='sessions-list'>
        {sessions.map((s, i) => (
          <div key={s.id || i} className='session-item' onClick={() => setViewingSession(s)}>
            <div className='session-item-header'>
              <span className='session-shopper'>{s.testShopperName || 'Unknown'}</span>
              <span className='session-date'>{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : ''}</span>
            </div>
            <div className='session-item-meta'>
              <span className='session-msgs'>{s.messageCount || 0} msgs</span>
              {s.humanGrade > 0 && (
                <span className='session-rating'>
                  {'★'.repeat(s.humanGrade)} {GRADE_LABELS[s.humanGrade]}
                </span>
              )}
              {s.isGoldenDataset && <span className='session-golden'>Golden</span>}
            </div>
            {s.humanNotes && (
              <div className='session-notes-preview'>
                {s.humanNotes.slice(0, 80)}
                {s.humanNotes.length > 80 ? '...' : ''}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'shopper-brief':
        return renderShopperBriefTab();
      case 'sessions':
        return renderSessionsTab();
      default:
        return null;
    }
  };

  return (
    <div className='dispatcher-brain'>
      <div className='brain-tabs'>
        {TABS.map(tab => (
          <button key={tab.key} className={cn('tab-btn', tab.className, { active: activeTab === tab.key })} onClick={() => handleTabChange(tab.key)}>
            {tab.label}
            {tab.key === 'shopper-brief' && brief && activeTab !== 'shopper-brief' && <span className='tab-dot' />}
            {tab.key === 'shopper-brief' && datakeeperResult && activeTab !== 'shopper-brief' && <span className='tab-dot bio-dot' />}
          </button>
        ))}
      </div>
      <div className='brain-content'>{renderTabContent()}</div>
      {viewingSession && <SessionViewer session={viewingSession} onClose={() => setViewingSession(null)} />}
    </div>
  );
};

export default DispatcherBrain;
